import {
  CheckCircleOutline,
  ErrorOutline,
  HelpOutline,
  HourglassEmpty,
  PlayCircleOutline,
  RemoveCircleOutline,
} from '@mui/icons-material';
import { ChipProps } from '@mui/material';
import { amber, grey, lightBlue } from '@mui/material/colors';

export const RUN_STATUSES = [
  'Queued',
  'Started',
  'Finished',
  'Failed',
  'Skipped',
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];
export type RunStatusOrUnknown = RunStatus | 'Unknown';

export interface IRunStatusDisplay {
  label: RunStatusOrUnknown;
  color: NonNullable<ChipProps['color']>;
  background?: string;
  icon: typeof HelpOutline;
  description: string;
}

// Each status differs by colour, icon and word; colour is never the only cue. Light badges carry black text for contrast.
export const RUN_STATUS_DISPLAY: Record<RunStatusOrUnknown, IRunStatusDisplay> =
  {
    Queued: {
      label: 'Queued',
      color: 'default',
      background: lightBlue[100],
      icon: HourglassEmpty,
      description: 'Run is queued',
    },
    Started: {
      label: 'Started',
      color: 'default',
      background: amber[200],
      icon: PlayCircleOutline,
      description: 'Run has started',
    },
    Finished: {
      label: 'Finished',
      color: 'success',
      icon: CheckCircleOutline,
      description: 'Run finished',
    },
    Failed: {
      label: 'Failed',
      color: 'error',
      icon: ErrorOutline,
      description: 'Run failed',
    },
    Skipped: {
      label: 'Skipped',
      color: 'default',
      background: grey[300],
      icon: RemoveCircleOutline,
      description: 'Run was skipped',
    },
    Unknown: {
      label: 'Unknown',
      color: 'default',
      icon: HelpOutline,
      description: 'Run status is unknown',
    },
  };

// Allowlist: any value outside the five words is Unknown.
export const toRunStatus = (value: unknown): RunStatusOrUnknown =>
  (RUN_STATUSES as readonly unknown[]).includes(value)
    ? (value as RunStatus)
    : 'Unknown';

export const REASON_CODES = [
  'SubscriptionInactive',
  'CascadeLoopClosed',
  'NoMatchingConnections',
  'PreflightFailed',
  'TargetScenarioIneligible',
  'TargetScenarioArchived',
  'TargetScenarioNotCreated',
  'TargetScenarioWrongClient',
  'CascadeDepthCap',
  'CascadeCycle',
  'EnqueueFailed',
] as const;
export type ReasonCode = (typeof REASON_CODES)[number];

// Exact membership on an array: prototype keys can never match.
export const toReasonCode = (value: unknown): ReasonCode | null =>
  REASON_CODES.find((code) => code === value) ?? null;
